import { createSelector } from 'reselect';

import {
  NOTIFICATION_TAB,
  FEED_TAB,
  COURSE_TAB,
  MISC_TAB,
} from '../../../native/containers/AppTabs/constants';
import {
  UNREAD_NOTIFICATION_SCREEN,
  ACTIVITY_FEED_SCREEN,
  MY_COURSES_SCREEN,
  MISCELLANEOUS_SCREEN,
} from '../../routeActions';
import { isLoggedIn } from '../../Auth/selectors';
import { selectAuthStackNavigatorState } from '../AuthStack/selectors';
import { selectNotificationStackNavigatorState } from '../NotificationTabStack/selectors';
import { selectFeedStackNavigatorState } from '../FeedTabStack/selectors';
import { selectCourseStackNavigatorState } from '../CourseTabStack/selectors';
import { selectMiscStackNavigatorState } from '../MiscTabStack/selectors';

const selectNavAppTabs = (state) => state.navAppTabs;

export const selectCurrentTabIndex = createSelector(
  [selectNavAppTabs],
  (appTabs) => {
    return appTabs.index;
  }
);

export const selectCurrentTabName = createSelector(
  [selectCurrentTabIndex],
  (activeIndex) => {
    switch (activeIndex) {
      case 0:
        return NOTIFICATION_TAB;
      case 1:
        return FEED_TAB;
      case 2:
        return COURSE_TAB;
      case 3:
        return MISC_TAB;
      default:
        return null;
    }
  }
);

export const selectIsInTabs = createSelector(
  [isLoggedIn, selectCurrentTabName],
  (loggedIn, currentActiveTab) => {
    if (loggedIn && currentActiveTab) {
      return true;
    }
    return false;
  }
);

export const selectCurrentTabStackIsEmpty = createSelector(
  [
    selectIsInTabs,
    selectCurrentTabName,
    selectAuthStackNavigatorState,
    selectNotificationStackNavigatorState,
    selectFeedStackNavigatorState,
    selectCourseStackNavigatorState,
    selectMiscStackNavigatorState,
  ],
  (
    isInTabs,
    activeTab,
    authStackState,
    notificationStackState,
    feedStackState,
    courseStackState,
    miscStackState
  ) => {
    if (isInTabs) {
      let activeStackState = null;
      switch (activeTab) {
        case NOTIFICATION_TAB:
          activeStackState = notificationStackState;
          break;
        case FEED_TAB:
          activeStackState = feedStackState;
          break;
        case COURSE_TAB:
          activeStackState = courseStackState;
          break;
        case MISC_TAB:
          activeStackState = miscStackState;
          break;
        default:
          break;
      }
      return activeStackState.nextId === 0;
    } else {
      return authStackState.nextId === 1;
    }
  }
);

export const selectCourseTabStackIsEmpty = createSelector(
  [selectCourseStackNavigatorState],
  (courseStackState) => {
    return courseStackState.nextId === 0;
  }
);

export const makeSelectTabStackNavigatorState = (tabName) =>
  createSelector(
    [
      selectNotificationStackNavigatorState,
      selectFeedStackNavigatorState,
      selectCourseStackNavigatorState,
      selectMiscStackNavigatorState,
    ],
    (
      notificationStackState,
      feedStackState,
      courseStackState,
      miscStackState
    ) => {
      switch (tabName) {
        case NOTIFICATION_TAB: {
          return notificationStackState;
        }
        case FEED_TAB: {
          return feedStackState;
        }
        case COURSE_TAB: {
          return courseStackState;
        }
        case MISC_TAB: {
          return miscStackState;
        }
        default:
          return null;
      }
    }
  );

export const selectPreviousRouteInTab = (tabName) =>
  createSelector(
    [makeSelectTabStackNavigatorState(tabName)],
    (tabStackState) => {
      if (tabStackState.history.length > 1) {
        const currentRouteIndex = tabStackState.history.length - 2;
        return tabStackState.history[currentRouteIndex];
      } else if (tabStackState.history.length === 1) {
        return tabStackState.history[0];
      } else {
        return null;
      }
    }
  );

export const makeSelectCurrentRouteInTab = (tabName) =>
  createSelector(
    [
      selectNotificationStackNavigatorState,
      selectFeedStackNavigatorState,
      selectCourseStackNavigatorState,
      selectMiscStackNavigatorState,
    ],
    (
      notificationStackState,
      feedStackState,
      courseStackState,
      miscStackState
    ) => {
      switch (tabName) {
        case NOTIFICATION_TAB:
          return notificationStackState.history[
            notificationStackState.history.length - 1
          ];
        case FEED_TAB:
          return feedStackState.history[feedStackState.history.length - 1];
        case COURSE_TAB:
          return courseStackState.history[courseStackState.history.length - 1];
        case MISC_TAB:
          return miscStackState.history[miscStackState.history.length - 1];
        default:
          return null;
      }
    }
  );

export const defaultRouteForTab = {
  [NOTIFICATION_TAB]: UNREAD_NOTIFICATION_SCREEN,
  [FEED_TAB]: ACTIVITY_FEED_SCREEN,
  [COURSE_TAB]: MY_COURSES_SCREEN,
  [MISC_TAB]: MISCELLANEOUS_SCREEN,
};
